import { css } from '@understory-io/leaf/css';
import type { ReactNode } from 'react';

type ExperienceGridProps = {
  /** Number of children. Drives the desktop column count: exactly 2 cards
   *  render in 2 columns, everything else (3+) renders in 3. The grid
   *  template is not derived from `Children.count` so a `null` slot doesn't
   *  silently collapse the layout. */
  count: number;
  children: ReactNode;
};

/**
 * How many columns the grid uses on desktop. Exported so the cards can size
 * their images off the same rule that draws the grid — the two going out of
 * sync is exactly how a `sizes` attribute ends up lying.
 */
export function desktopColumnCount(count: number): 2 | 3 {
  return count === 2 ? 2 : 3;
}

/**
 * The cards whose images are fetched up front at high priority: the first
 * row's worth. Any of them can end up being the LCP element — which one wins
 * depends on the viewport and on the images' own dimensions — so the whole row
 * is treated as urgent rather than guessing a single winner.
 *
 * Chosen among the cards that actually *have* a cover. An experience without
 * one renders a placeholder, no `<img>` at all, so counting it would spend a
 * slot on nothing. That is not hypothetical: the first experience on a
 * storefront frequently has no cover, and keying off position instead left the
 * real LCP image with no priority at all.
 */
export function priorityImageIds(
  experiences: readonly { id: string; image: string | null }[],
  columns: number
): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const experience of experiences) {
    if (!experience.image) continue;
    ids.add(experience.id);
    if (ids.size === columns) break;
  }
  return ids;
}

/**
 * Responsive layout for the home page experience tiles.
 *
 * - Mobile (base): 1 column, full width.
 * - Tablet: 2 columns (50/50).
 * - Desktop with 2 cards: 2 columns (matches tablet so the row stays paired).
 * - Desktop with 3+ cards: 3 columns.
 */
export function ExperienceGrid({ count, children }: ExperienceGridProps) {
  return (
    <ul
      className={css({
        listStyle: 'none',
        margin: 'none',
        padding: 'none',
        display: 'grid',
        gap: 'large',
        width: 'full',
        gridTemplateColumns: {
          base: '1fr',
          tablet: 'repeat(2, 1fr)',
          // Both branches stay string literals so Panda's static extractor
          // still emits the two grid-template classes.
          desktop:
            desktopColumnCount(count) === 2
              ? 'repeat(2, 1fr)'
              : 'repeat(3, 1fr)',
        },
      })}
    >
      {children}
    </ul>
  );
}
